import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { KPI61Icon, KPI81Icon } from '../../../assets/icons';
import { formatAmount, formatMiles } from '../../../utils';
import { DashboardService } from '../../../services';

const colors = {
  orange: ['#F67B00','#FEAA57','#FFDB65',],
  red: ['#FF0000','#FF6C6C','#FFAD8F'],
  green: ['#16DB62','#33FF82','#AEFFCD'],
  blue: ['#396AFF','#7EB2FF','#89CEFF'],
}

const Home = () => {
  const { chart1, chart2, currentMonth, data } = useData();

  return (
    <main className="grid grid-cols-2 gap-6">
      <section className="bg-white shadow-md rounded-2xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold">Fallas</span>
            <span className="text-xl text-red ml-4">{ currentMonth }</span>
          </div>
          <div>
            <img className="w-10 h-10" src={KPI61Icon} alt="" />
          </div>
        </div>

        <ReactApexChart
          width={480}
          height={200}
          options={chart1.options}
          series={chart1.series}
          type="donut"
        />

        <div className="flex justify-end">
          <Link
            className="bg-[#000] text-white text-lg font-bold px-4 py-1.5 rounded-lg"
            to="/report/failures"
          >
            Ver reporte
          </Link>
        </div>
      </section>

      <section className="bg-white shadow-md rounded-2xl p-4 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold">Mantenimientos</span>
            <span className="text-xl text-red ml-4">{ currentMonth }</span>
          </div>
          <div>
            <img className="w-10 h-10" src={KPI81Icon} alt="" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 flex-1">
          <div className="border-2 border-red rounded-lg px-2 py-1 space-y-4 max-h-min">
            <p className="text-xl font-bold">Pendientes</p>
            <h1 className="text-6xl font-bold font-sans">{ data.maintenances?.pending || 0 }</h1>
          </div>

          <div className="border-2 border-orange rounded-lg px-2 py-1 space-y-4 max-h-min">
            <p className="text-xl font-bold">En mantenimiento</p>
            <h1 className="text-6xl font-bold font-sans">{ data.maintenances?.in_process || 0 }</h1>
          </div>
        </div>

        <Link
          className="bg-[#000] text-white text-lg font-bold px-4 py-1.5 rounded-lg self-start"
          to="/maintenance"
        >
          Ver reporte
        </Link>
      </section>

      <section className="bg-white shadow-md rounded-2xl p-4 space-y-6 col-span-2">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold">Gastos mantenimientos</span>
            <span className="text-xl text-red ml-4">{ currentMonth }</span>
          </div>
          <div>
            <Link
              className="bg-[#000] text-white text-lg font-bold px-4 py-2 rounded-lg"
              to="/maintenance/history"
            >
              Ver reporte
            </Link>
          </div>
        </div>

        <div className="flex">
          <div className="w-1/4 space-y-2 overflow-y-auto max-h-96">
            <div className="bg-orange rounded-lg text-white p-2">
              <p className="text-lg font-semibold">Total de gastos</p>
              <p className="text-4xl font-bold">{ formatAmount(data.expenses.total, '$') }</p>
              <p className="text-xl font-bold opacity-80">{ formatAmount(data.expenses.total_local, 'Bs ') }</p>
            </div>

            <div className="bg-[#000] rounded-lg text-white p-2">
              <p className="text-lg font-semibold">Cantidad de mantenimientos culminados</p>
              <p className="text-4xl font-bold">{ data.maintenances?.finished || 0 }</p>
            </div>

            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-orange mb-1">Repuestos</p>

              {data.expenses.series?.parts?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.orange.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-red mb-1">Categorías</p>

              {data.expenses.series?.categories?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.red.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-green mb-1">Proveedor</p>

              {data.expenses.series?.providers?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.green.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-[#396AFF] mb-1">Unidad</p>

              {data.expenses.series?.units?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.blue.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1">
            <ReactApexChart
              options={chart2.options}
              series={chart2.series}
              type="bar"
              height={350}
            />
          </div>
        </div>
      </section>
    </main>
  );
}

const useData = () => {
  const currentMonth = moment().format('MMMM');
  const [data, setData] = useState({
    failures: {
      pending: 0,
      resolved: 0,
    },
    maintenances: {
      pending: 0,
      in_process: 0,
      finished: 0,
    },
    expenses: {
      total: 0,
      total_local: 0,
      series: {
        parts: [],
        categories: [],
        providers: [],
        units: [],
      },
    }
  });
  const [chart1, setChart1] = useState({
    series: [],
    options: {
      colors: ['#396AFF', '#16DB62'],
      labels: ['Pendientes', 'Resueltas'],
      dataLabels: {
        enabled: false
      },
      legend: {
        fontSize: 28,
        fontWeight: 700,
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                showAlways: true,
                show: true,
                label: '',
                style: {
                  fontSize: '28px',
                }
              },
            },
          },
        },
      },
    },
  });
  const [chart2, setChart2] = useState({
    series: [],
    options: {
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: {
          show: false
        }
      },
      dataLabels: {
        enabled: false
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '75%',
          borderRadius: 12,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      grid: {
        borderColor: '#e5e7eb',
        yaxis: {
          lines: { show: true }
        },
        xaxis: {
          lines: { show: false }
        }
      },
      yaxis: {
        tickAmount: 11,
        labels: {
          style: {
            colors: '#9ca3af',
            fontSize: '14px',
          }
        }
      },
      xaxis: {
        labels: {
          show: false,
        },
      },
      fill: {
        opacity: 1
      },
      legend: {
        show: false
      },
      tooltip: {
        custom: function (opts) {
          const w = opts.w;
          const serie = w.config.series[opts.seriesIndex];
          let serieName = '';
          let value = '';
          let color = '';
          let ylabel = '';

          const data = serie?.data?.at(opts.dataPointIndex);
          if (data) {
            serieName = data?.meta?.name || serie?.name || '';
            ylabel = data?.x;
            value = data?.y;
            color = data?.fillColor;
          } else {
            serieName = serie?.name || '';
          }

          return (
            '<div class="apexcharts-tooltip-rangebar">' +
              '<div class="flex items-center">'+
                '<div class="apexcharts-tooltip-marker" style="border-radius: 20px;background-color: ' + color + '"></div>' +
                '<span class="series-name pt-1">' +
                  ylabel +
                '</span>' +
              '</div>' +
              '<div> <span class="category">' +
                serieName +
              ': </span>' +
              '<span class="value start-value">' +
                value +
              '</span>' +
            '</div>'
          )
        },
      },
    },
  });

  const fetchData = async () => {
    try {
      const response = await DashboardService.coordinator.findAll();
      setData(response);
      setChart1(s => ({
        ...s,
        series: [
          Number(response.failures?.pending || 0),
          Number(response.failures?.resolved || 0),
        ],
        options: {
          ...s.options,
          labels: [
            `Pendientes (${response.failures?.pending || 0})`,
            `Resueltas (${response.failures?.resolved || 0})`,
          ]
        },
      }));

      const chart2Series = [
        {
          name: 'Nivel Inferior',
          data: []
        },
        {
          name: 'Nivel Medio',
          data: []
        },
        {
          name: 'Nivel Superior',
          data: []
        }
      ];

      [
        [response.expenses.series.parts, colors.orange, 'Repuestos'],
        [response.expenses.series.categories, colors.red, 'Categorías'],
        [response.expenses.series.providers, colors.green, 'Proveedor'],
        [response.expenses.series.units, colors.blue, 'Unidad'],
      ].forEach(([serie, color, label], groupIdx) => {
        serie.forEach((item, itemIdx) => {
          chart2Series.at(2 - itemIdx).data.push({
            x: label,
            y: item.value,
            fillColor: color.at(itemIdx),
            meta: { name: item.label || '' },
          });
        });
      });

      setChart2(s => ({ ...s, series: chart2Series }));

    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  return {
    chart1,
    chart2,
    currentMonth,
    data,
  };
}

export default Home;
